import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Container, Modal, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import MUIDataTable from "mui-datatables";
import ClienteService from "../services/cliente.service";
import ComprobantesService from "../services/comprobantes.service";
import PagosCuentasCorrientesService from "../services/pagos_cuentas_corrientes.service";
import CuentaCorrienteService from "../services/cc.service";
import EventBus from "../common/EventBus";
import { toast } from "react-toastify";

const dayjs = require("dayjs");

const nf = new Intl.NumberFormat("es-AR", {
  style: "decimal",
  currency: "ARS",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const ClientesInactivos = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  // Modal cliente
  const [showModal, setShowModal] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [comprobantes, setComprobantes] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);
  const [activeTab, setActiveTab] = useState("facturas");
  const [errorModal, setErrorModal] = useState("");

  useEffect(() => {
    const userCrudo = localStorage.getItem("user");
    if (!userCrudo) {
      navigate("/home");
      return;
    }
    const user = JSON.parse(userCrudo);
    const admin = user?.roles?.includes("ROLE_ADMIN") ?? false;
    if (!admin) {
      navigate("/home");
      return;
    }
    setIsAdmin(true);
  }, [navigate]);

  useEffect(() => {
    if (!isAdmin) return;

    const fetchInactivos = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await ClienteService.searchInactivos();
        setClientes(response.data || []);
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Error al cargar los clientes inactivos.";
        setError(msg);
        if (err?.response?.status === 401) {
          EventBus.dispatch("logout");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInactivos();
  }, [isAdmin]);

  const handleRowClick = async (rowData) => {
    const idCliente = rowData[0];
    setLoadingModal(true);
    setErrorModal("");
    setShowModal(true);
    setActiveTab("facturas");
    setComprobantes([]);
    setPagos([]);

    let cliente = null;
    let idCuentaCorriente = null;
    let cuentaCorriente = null;
    // 1. Obtener datos del cliente
    try {
      const respCliente = await ClienteService.getClienteByID(idCliente);
      cliente = respCliente.data;
      setClienteSeleccionado(cliente);
      cuentaCorriente = await CuentaCorrienteService.getCuentaCorrienteByIdCliente(idCliente);
      idCuentaCorriente = cuentaCorriente?.data?.idCuentaCorriente;
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Error al obtener datos del cliente.";
      setErrorModal(`[Cliente] ${msg}`);
      if (err?.response?.status === 401) {
        EventBus.dispatch("logout");
      }
      setLoadingModal(false);
      return;
    }

    if (!cuentaCorriente || !cuentaCorriente.data) {
      setErrorModal("El cliente no tiene cuenta corriente asociada.");
      setLoadingModal(false);
      return;
    }

    // 2. Obtener comprobantes
    try {
      const respComp = await ComprobantesService.getComprobantes(idCliente);
      setComprobantes(respComp.data || []);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Error al cargar comprobantes.";
      toast.error(`[Comprobantes] ${msg}`, { position: "top-center", autoClose: 3000 });
      if (err?.response?.status === 401) {
        EventBus.dispatch("logout");
      }
    }

    // 3. Obtener pagos
    try {
      const respPagos = await PagosCuentasCorrientesService.getPagos(idCuentaCorriente);
      setPagos(respPagos.data || []);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Error al cargar pagos.";
      toast.error(`[Pagos] ${msg}`, { position: "top-center", autoClose: 3000 });
      if (err?.response?.status === 401) {
        EventBus.dispatch("logout");
      }
    }

    setLoadingModal(false);
  };

  const columnsClientes = [
    {
      name: "idCliente",
      label: "ID",
      options: { display: "excluded" },
    },
    {
      name: "RazonSocial",
      label: "Razón Social",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>{value}</Row>
        ),
      },
    },
    {
      name: "Direccion",
      label: "Dirección",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>{value || "-"}</Row>
        ),
      },
    },
    {
      name: "Localidad",
      label: "Localidad",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>{value || "-"}</Row>
        ),
      },
    },
    {
      name: "Telefono",
      label: "Teléfono",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>{value || "-"}</Row>
        ),
      },
    },
    {
      name: "Mail",
      label: "Email",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>{value || "-"}</Row>
        ),
      },
    },
    {
      name: "Cuit",
      label: "CUIT",
      options: {
        customBodyRender: (value) => (
          <Row style={{ paddingLeft: 30 }}>{value || "-"}</Row>
        ),
      },
    },
  ];

  const optionsClientes = {
    filter: false,
    search: true,
    searchOpen: true,
    searchAlwaysOpen: true,
    searchPlaceholder: "Buscar cliente",
    download: false,
    print: false,
    selectableRows: false,
    rowsPerPage: 100,
    rowsPerPageOptions: [100],
    responsive: "standard",
    elevation: 0,
    enableNestedDataAccess: ".",
    onRowClick: handleRowClick,
    textLabels: {
      body: {
        noMatch: loading ? "Cargando..." : "No se encontraron clientes inactivos.",
      },
    },
  };

  const columnsComprobantes = [
    {
      name: "idPp",
      label: "ID PP",
      options: { display: "excluded" },
    },
    {
      name: "idFactura",
      label: "ID FF",
      options: { display: "excluded" },
    },
    {
      name: "idDevoluciones",
      label: "ID NC",
      options: { display: "excluded" },
    },
    {
      name: "codigo",
      label: "Codigo",
      options: {
        customBodyRender: (value, tableMeta) => {
          let tipo = "";
          if (tableMeta.rowData[0] !== undefined) {
            let auxie = value.toString();
            return "PP - 0001-" + auxie.padStart(8, "0");
          }
          if (tableMeta.rowData[1] !== undefined) {
            if (tableMeta.rowData[6] === 1) {
              tipo = "FA";
            }
            if (tableMeta.rowData[6] === 6) {
              tipo = "FB";
            }
          }
          if (tableMeta.rowData[2] !== undefined) {
            if (tableMeta.rowData[6] === 1) {
              tipo = "NCA";
            }
            if (tableMeta.rowData[6] === 6) {
              tipo = "NCB";
            }
          }
          let aux = value.split("-");
          let pv = aux[0].padStart(4, "0");
          let nro = aux[1].padStart(8, "0");
          return tipo + " - " + pv + "-" + nro;
        },
      },
    },
    {
      name: "fecha",
      label: "Fecha",
      options: {
        customBodyRender: (value) => {
          return dayjs(value).format("DD/MM/YYYY H:m:ss");
        },
      },
    },
    {
      name: "importe",
      label: "Importe",
      options: {
        customBodyRender: (value) => {
          return nf.format(value);
        },
      },
    },
    {
      name: "tipoFactura",
      label: "tipoFactura",
      options: { display: "excluded" },
    },
  ];

  const optionsComprobantes = {
    filter: false,
    search: true,
    searchOpen: true,
    searchAlwaysOpen: true,
    searchPlaceholder: "Buscar comprobantes",
    download: false,
    print: false,
    selectableRows: false,
    rowsPerPage: 100,
    rowsPerPageOptions: [100],
    responsive: "standard",
    elevation: 0,
    enableNestedDataAccess: ".",
  };

  const columnsPagos = [
    {
      name: "idPago",
      label: "ID Pago",
      options: { display: "excluded" },
    },
    { name: "Tipo", label: "Tipo" },
    {
      name: "Fecha",
      label: "Fecha de Carga",
      options: {
        customBodyRender: (value) => {
          return dayjs(value).format("DD/MM/YYYY HH:mm");
        },
      },
    },
    {
      name: "Total",
      label: "Total",
      options: {
        customBodyRender: (value) => {
          return "$ " + nf.format(value);
        },
      },
    },
  ];

  const optionsPagos = {
    filter: false,
    search: true,
    searchOpen: true,
    searchAlwaysOpen: true,
    searchPlaceholder: "Buscar pagos",
    download: false,
    print: false,
    selectableRows: false,
    rowsPerPage: 100,
    rowsPerPageOptions: [100],
    responsive: "standard",
    elevation: 0,
    enableNestedDataAccess: ".",
  };

  if (!isAdmin) {
    return (
      <Container className="admin-mobile-root">
        <h3 className="admin-title">Clientes Inactivos</h3>
        <Card className="admin-surface">
          <Card.Body>
            <Alert variant="warning" className="mb-0">No tenés permisos para acceder a esta página.</Alert>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="admin-mobile-root">
      <h3 className="admin-title">Clientes Inactivos</h3>

      <Card className="admin-surface mb-2">
        <Card.Body>
          <div className="d-flex justify-content-end mb-2">
            <Button
              variant="light"
              className="admin-action-btn"
              style={{ minHeight: 38, borderRadius: 10, padding: "6px 14px" }}
              onClick={() => navigate("/admin")}
            >
              Volver
            </Button>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <Row className="justify-content-center py-4">
              <Spinner animation="border" role="status" />
            </Row>
          ) : (
            <MUIDataTable
              title={"Listado de clientes inactivos"}
              data={clientes}
              columns={columnsClientes}
              options={optionsClientes}
            />
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        fullscreen={true}
        onHide={() => setShowModal(false)}
      >
        <Modal.Header>
          <Modal.Title>
            {clienteSeleccionado?.RazonSocial || "Cliente"}
          </Modal.Title>
          <Button
            variant="outline-danger"
            onClick={() => setShowModal(false)}
          >
            X
          </Button>
        </Modal.Header>
        <Modal.Body>
          {loadingModal ? (
            <Row className="justify-content-center py-4">
              <Spinner animation="border" role="status" />
            </Row>
          ) : (
            <>
              {errorModal && (
                <Alert variant="danger">{errorModal}</Alert>
              )}

              <Alert variant="danger" className="mb-3">
                <b>Atención:</b> Cliente inactivo
              </Alert>

              {clienteSeleccionado && (
                <Row className="mb-3">
                  <Col>
                    <Card className="admin-note-card">
                      <Card.Body>
                        <Row>
                          <Col xs={12} md={6}>
                            <p className="mb-1">
                              <b>Código:</b> {clienteSeleccionado.cliente.Codigo || "-"}
                            </p>
                            <p className="mb-1">
                              <b>CUIT:</b> {clienteSeleccionado.cliente.Cuit || "-"}
                            </p>
                            <p className="mb-1">
                              <b>Dirección:</b> {clienteSeleccionado.cliente.Direccion || "-"}
                            </p>
                          </Col>
                          <Col xs={12} md={6}>
                            <p className="mb-1">
                              <b>Teléfono:</b> {clienteSeleccionado.cliente.Telefono || "-"}
                            </p>
                            <p className="mb-1">
                              <b>Email:</b> {clienteSeleccionado.cliente.Email || "-"}
                            </p>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                id="cliente-inactivo-tabs"
                className="mb-3"
              >
                <Tab eventKey="facturas" title="Facturas">
                  <MUIDataTable
                    title={"Lista de Comprobantes"}
                    data={comprobantes}
                    columns={columnsComprobantes}
                    options={optionsComprobantes}
                  />
                </Tab>
                <Tab eventKey="historial" title="Historial">
                  <MUIDataTable
                    title={"Lista de Pagos"}
                    data={pagos}
                    columns={columnsPagos}
                    options={optionsPagos}
                  />
                </Tab>
              </Tabs>
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ClientesInactivos;
